import dayjs from 'dayjs';

import { constantsConfig } from '../config/config.bundle';
import {
  AVAILABILITY_HORIZON,
  deliveryMethodLocationTypeMap,
  DPM_CUSTOMER_CLASS_CODE,
  UNKNOWN_AVAILABILITY_VALUE,
} from '../constants/core-constants';
import {
  INTERNATIONAL_CUSTOMER_SUBCLASSES,
  PARTS_UNKNOWN_AVAILABLE_QUANTITY,
} from '../constants/parts-constants';
import customerNetApi from '../store/apis/cnet-api';
import productApi from '../store/apis/product-api';
import {
  activeAccountSelector,
  activePropertySelector,
  activeUserSelector,
  priceSubTypesSelector,
  salesAgreementsSelector,
} from '../store/selectors/session-selectors';
import store, { type AppRootState } from '../store/store';
import type {
  MenuItem,
  OrderAvailability,
  PartAvailability,
  Pim,
  Pricing,
  PricingV2,
  ProductDetails,
  SalesAgreement,
  ShippingLocation,
} from '../types/api-types';
import { isOktaAdminEmployeeUser } from './auth-utils';

export const getAllProductCategories = () => {
  const state = store.getState() as AppRootState;
  const menu = productApi.endpoints.menu.select()(state).data;

  const categories: MenuItem[] = [];

  const menuItem = menu?.find(menuItem => menuItem.id === 'shop-all-products');

  if (menuItem) {
    const loadCategory = (menuItems: MenuItem[]) => {
      menuItems.forEach(menuItem => {
        if (
          menuItem.children?.length &&
          menuItem.children.some(subCategory => subCategory.children?.length)
        ) {
          loadCategory(menuItem.children);
        } else {
          categories.push(menuItem);
        }
      });
    };

    if (menuItem.children) {
      loadCategory(menuItem.children);
    }
  }

  return categories;
};

export const getSalesAgreement = (
  agreementType: SalesAgreement['AGREEMENT_TYPE'],
  useBackup?: boolean
): SalesAgreement & {
  ORIGINAL_ACCOUNT_NUMBER?: string;
  ORIGINAL_AGREEMENT_NUMBER?: number;
} => {
  const state = store.getState() as AppRootState;
  const activeAccount = activeAccountSelector(state);
  const activeUser = activeUserSelector(state);
  const salesAgreements = salesAgreementsSelector(state);

  if (
    isOktaAdminEmployeeUser(activeUser) &&
    activeUser?.selectedSalesAgreementNumber
  ) {
    const selectedSalesAgreement = salesAgreements?.find(
      salesAgreement =>
        (agreementType === 'GEA Parts'
          ? salesAgreement.AGREEMENT_TYPE === agreementType
          : salesAgreement.AGREEMENT_NUMBER.toString() ===
            activeUser.selectedSalesAgreementNumber) &&
        (!activeUser.selectedAccountNumber ||
          salesAgreement.ACCOUNT_NUMBER === activeUser.selectedAccountNumber) &&
        salesAgreement.STATUS === 'ACTIVE'
    );

    if (selectedSalesAgreement) {
      if (
        useBackup &&
        activeAccount?.CUSTOMERCLASSCODE === DPM_CUSTOMER_CLASS_CODE &&
        dayjs()
          .tz('America/New_York')
          .diff(
            dayjs.tz(
              selectedSalesAgreement?.ACTIVATION_DATE,
              'America/New_York'
            ),
            'days'
          ) < constantsConfig.pricingJobIntervalDays
      ) {
        return {
          ...selectedSalesAgreement,
          ACCOUNT_NUMBER: constantsConfig.dpmData.fallbackAccountNumber,
          ORIGINAL_ACCOUNT_NUMBER: selectedSalesAgreement.ACCOUNT_NUMBER,
          AGREEMENT_NUMBER:
            constantsConfig.dpmData.fallbackSalesAgreementNumber,
          ORIGINAL_AGREEMENT_NUMBER: selectedSalesAgreement.AGREEMENT_NUMBER,
        };
      }

      return selectedSalesAgreement;
    }
  }

  const salesAgreement = salesAgreements?.find(
    (salesAgreement: SalesAgreement) =>
      salesAgreement.AGREEMENT_TYPE === agreementType &&
      salesAgreement.STATUS === 'ACTIVE'
  );

  if (
    useBackup &&
    activeAccount?.CUSTOMERCLASSCODE === DPM_CUSTOMER_CLASS_CODE &&
    dayjs()
      .tz('America/New_York')
      .diff(
        dayjs.tz(salesAgreement?.ACTIVATION_DATE, 'America/New_York'),
        'days'
      ) < constantsConfig.pricingJobIntervalDays
  ) {
    return {
      ...salesAgreement!,
      ACCOUNT_NUMBER: constantsConfig.dpmData.fallbackAccountNumber,
      ORIGINAL_ACCOUNT_NUMBER: salesAgreement?.ACCOUNT_NUMBER,
      AGREEMENT_NUMBER: constantsConfig.dpmData.fallbackSalesAgreementNumber,
      ORIGINAL_AGREEMENT_NUMBER: salesAgreement?.AGREEMENT_NUMBER,
    };
  }
  return salesAgreement!;
};

export const getShippingLocations = async () => {
  const activeProperty = activePropertySelector(
    store.getState() as AppRootState
  );
  const activeAccount = activeAccountSelector(store.getState() as AppRootState);

  if (!activeProperty || !activeAccount) {
    return [];
  }

  const shippingLocationsResponse = await store
    .dispatch(
      customerNetApi.springBoot.endpoints.shippingLocations.initiate({
        accountNumber: activeAccount.ACCOUNTNUMBER,
        state: activeProperty.STATE,
        countryCode: activeProperty.COUNTRY,
        postalCode: activeProperty.POSTAL_CODE.split('-')[0],
      })
    )
    .unwrap();
  return shippingLocationsResponse.responseObject;
};

export const getShippingLocation = async (
  locationType: ShippingLocation['loctype']
) => {
  const shippingLocations = await getShippingLocations();
  const shippingLocation = shippingLocations.find(
    shippingLocation => shippingLocation.loctype === locationType
  );

  return shippingLocation;
};

export const getItemPoints = (itemPim: Pim, quantity: number) => {
  return (Number(itemPim?.geego_top_load) || 0) * quantity;
};

export const getItemWeight = (itemPim: Pim, quantity: number) => {
  return (Number(itemPim?.geego_carton_gross_weight) || 0) * quantity;
};

export const getAccessorySkus = (productDetails: ProductDetails) => {
  const accessorySkus: string[] = [];
  const accessories =
    productDetails.relationships?.find(
      productRelationship => productRelationship.name === 'Accessories_'
    )?.items || [];

  const accessoryKits =
    productDetails.relationships?.find(
      productRelationship => productRelationship.name === 'Accessories Kit_'
    )?.items || [];

  [...accessories, ...accessoryKits].forEach(item => {
    if (!accessorySkus.includes(item.sku)) {
      accessorySkus.push(item.sku);
    }
  });

  productDetails.optionalCharge?.split('|').forEach(sku => {
    if (!accessorySkus.includes(sku)) {
      accessorySkus.push(sku);
    }
  });

  return accessorySkus;
};

type BagItem = {
  serviceId: string;
  serviceName: string;
  quantity: string;
  price: string;
  extendedPrice: string;
  serviceType: string;
  tariffBagItem?: boolean;
  priceSubType: string;
  productCode: string;
};

export const getBagItemsFromPricing = (pricing: Pricing) => {
  const bagItems: BagItem[] = [];
  const tariffBagItems: BagItem[] = [];

  pricing.itemPrice.discounts?.discount?.forEach(discount => {
    if (
      discount.bagitem &&
      discount.getItemAdjUnitPrice &&
      discount.getLineQty &&
      discount.getLineTotal
    ) {
      bagItems.push({
        serviceId: discount.bagitem,
        serviceName: discount.bagitem,
        quantity: (discount.getLineQty || 0).toString(),
        price: (discount.getItemAdjUnitPrice
          ? parseFloat(discount.getItemAdjUnitPrice).toFixed(2)
          : 0
        ).toString(),
        extendedPrice: (discount.getLineTotal || 0).toString(),
        serviceType: 'BAG',
        priceSubType: discount.priceSubType,
        productCode: '',
      });
    } else if (
      discount.name &&
      discount.name.toUpperCase().includes('TARIFF')
    ) {
      tariffBagItems.push({
        serviceId: discount.chargeTypeCode,
        serviceName: discount.chargeTypeCode,
        quantity: (discount.parentLineQty || 0).toString(),
        price: (discount.amount
          ? parseFloat(discount.amount).toFixed(2)
          : 0
        ).toString(),
        extendedPrice: (discount.adjustedCharge || 0).toString(),
        serviceType: 'BAG',
        tariffBagItem: true,
        priceSubType: discount.priceSubType,
        productCode: '',
      });
    }
  });

  return bagItems.concat(tariffBagItems);
};

export const getBagItemsFromPricingV2 = (
  pricingLineItem: PricingV2['priceResponse'][number],
  pricing: PricingV2
) => {
  const bagItems: BagItem[] = [];
  const priceSubTypesExclusionList =
    priceSubTypesSelector(store.getState() as AppRootState)?.map(
      priceSubTypeData => priceSubTypeData.priceSubType
    ) || [];

  pricingLineItem.discounts?.forEach(discount => {
    if (
      discount.bagItem &&
      !priceSubTypesExclusionList.includes(discount.priceSubType) &&
      !bagItems.some(bagItem => bagItem.serviceName === discount.bagItem)
    ) {
      const bagLineItem = pricing.priceResponse.find(
        lineItem =>
          lineItem.item === null &&
          lineItem.discounts.some(
            lineItemDiscount => lineItemDiscount.itemName === discount.bagItem
          )
      );
      if (bagLineItem) {
        bagItems.push({
          serviceId: discount.bagItem,
          serviceName: discount.bagItem,
          priceSubType: discount.priceSubType,
          quantity: bagLineItem.qty.toString(),
          price: bagLineItem.salesPrice.toString(),
          extendedPrice: bagLineItem.extendedPrice.toString(),
          serviceType: 'BAG',
          productCode: '',
        });
      }
    }
  });

  return bagItems;
};

export const getLatestShipDateFromOrderAvailability = (
  availability: OrderAvailability[]
) => {
  return (
    availability.reduce((acc, cur) =>
      dayjs(cur.shipDate).isAfter(acc.shipDate) ? cur : acc
    )?.shipDate || null
  );
};

export const getDeliveryCalendarType = (
  deliveryMethod:
    | 'SDS Delivery'
    | 'SDS Will Call'
    | 'ADC Truckload'
    | 'Meet Truck'
    | 'ADC LTL'
    | (string & {})
) => {
  if (
    deliveryMethod.toUpperCase().includes('ADC')
    // deliveryMethod.toUpperCase().includes('MEET TRUCK')
  ) {
    return 'ADC';
  } else if (deliveryMethod.toUpperCase().includes('SDS')) {
    return 'SDS';
  } else {
    return null;
  }
};

export const getLocationTypeByDeliveryMethod = (
  deliveryMethod: keyof typeof deliveryMethodLocationTypeMap | (string & {})
) => {
  if (
    deliveryMethodLocationTypeMap[
      deliveryMethod as keyof typeof deliveryMethodLocationTypeMap
    ]
  ) {
    return deliveryMethodLocationTypeMap[
      deliveryMethod as keyof typeof deliveryMethodLocationTypeMap
    ];
  } else {
    return null;
  }
};

export const validateFinalProduction = (data: {
  requestedQuantity: number;
  finalProductionDate?: string | null;
  availableQuantity?: string | null;
  eta?: string | null;
}) => {
  return (
    dayjs(data.finalProductionDate).diff(dayjs(), 'days') < 0 &&
    (!data.availableQuantity ||
      !+data.availableQuantity ||
      data.availableQuantity === UNKNOWN_AVAILABILITY_VALUE.toString() ||
      +data.availableQuantity < data.requestedQuantity) &&
    dayjs(data.eta).diff(dayjs(), 'days') > AVAILABILITY_HORIZON
  );
};

const SKU_RULES = {
  move_code: /^M\d{3}$/,
};

export const isSkuCategory = (sku: string, category: keyof typeof SKU_RULES) =>
  SKU_RULES[category].test(sku);

/**
 * Counts back N business days (Mon–Fri) from a given date.
 */
export const subtractBusinessDays = (
  date: string,
  days: number,
  timezone?: string
): dayjs.Dayjs => {
  let count = 0;
  let cursor = (timezone ? dayjs.tz(date, timezone) : dayjs(date)).startOf(
    'day'
  );
  while (count < days) {
    cursor = cursor.subtract(1, 'day');
    const dow = cursor.day();
    if (dow !== 0 && dow !== 6) count++;
  }
  return cursor;
};

export const getPimRelatedItemNumbers = (
  pim: Pim,
  relationshipType: 'Superseded'
) => {
  const relatedItemNumbers: string[] = [];
  const relationshipTypes = Array.isArray(pim.relationship_type)
    ? pim.relationship_type
    : pim.relationship_type
      ? [pim.relationship_type]
      : [];
  const relatedSkus = Array.isArray(pim.related_item_number)
    ? pim.related_item_number
    : pim.related_item_number
      ? [pim.related_item_number]
      : [];
  const relationShipStartDates = Array.isArray(pim.relationship_start_date)
    ? pim.relationship_start_date
    : pim.relationship_start_date
      ? [pim.relationship_start_date]
      : [];
  const relationShipEndDates = Array.isArray(pim.relationship_end_date)
    ? pim.relationship_end_date
    : pim.relationship_end_date
      ? [pim.relationship_end_date]
      : [];
  if (relationshipType === 'Superseded') {
    relationshipTypes.forEach((pimRelationshipType, index) => {
      if (pimRelationshipType === relationshipType) {
        if (
          !relationShipStartDates[index] ||
          relationShipStartDates[index] === 'NULL' ||
          (relationShipStartDates[index] !== 'NULL' &&
            dayjs(relationShipStartDates[index], 'DD-MMM-YYYY') <=
              dayjs(dayjs().format('DD-MMM-YYYY'), 'DD-MMM-YYYY'))
        ) {
          if (
            !relationShipEndDates[index] ||
            relationShipEndDates[index] === 'NULL' ||
            (relationShipEndDates[index] !== 'NULL' &&
              dayjs(relationShipEndDates[index], 'DD-MMM-YYYY') >
                dayjs(dayjs().format('DD-MMM-YYYY'), 'DD-MMM-YYYY'))
          ) {
            relatedItemNumbers.push(relatedSkus[index]);
          }
        }
      }
    });
  } else if (relationshipType === 'Substitute') {
    relationshipTypes.forEach((pimRelationshipType, index) => {
      if (pimRelationshipType === relationshipType) {
        relatedItemNumbers.push(relatedSkus[index]);
      }
    });
  }

  return relatedItemNumbers;
};

export const getPimItemType = (pim: Pim) => {
  let itemType: 'PART' | 'ACCESSORY' | 'APPLIANCE' | 'SERVICE' | null = null;
  if (pim.icc_hierarchy?.includes('Service Parts')) {
    itemType = 'PART';
  } else if (pim.geego_accessory_indicator === 'Y') {
    itemType = 'ACCESSORY';
  } else if (pim.icc_hierarchy?.includes('Appliances Finished Goods')) {
    itemType = 'APPLIANCE';
  } else if (pim.icc_hierarchy?.includes('Services')) {
    itemType = 'SERVICE';
  }

  return itemType;
};

export const getCustomerNetItemType = (
  itemType?: 'PART' | 'ACCESSORY' | 'APPLIANCE' | 'SERVICE' | null
) => {
  switch (itemType) {
    case 'PART':
      return 'P';
    case 'ACCESSORY':
      return 'A';
    case 'APPLIANCE':
      return 'M';
    case 'SERVICE':
      return 'S';
    default:
      return itemType;
  }
};

export const isInternationalPartsCustomer = (
  subAccountClassification: string,
  country: string
) =>
  INTERNATIONAL_CUSTOMER_SUBCLASSES.includes(
    subAccountClassification as (typeof INTERNATIONAL_CUSTOMER_SUBCLASSES)[number]
  ) ||
  (subAccountClassification === 'PARTS-AWD' && country === 'CA');

export const normalizePartsAvailability = (availability: PartAvailability) => ({
  availableQuantity: availability.availableQuantity?.toString() || '0',
  requestedDateQuantity: availability.requestedDateQuantity
    ? availability.requestedDateQuantity >= PARTS_UNKNOWN_AVAILABLE_QUANTITY
      ? '0'
      : availability.requestedDateQuantity?.toString()
    : '0',
  arrivalDate: availability.arrivalDate || '',
  groupArrivalDate: availability.groupArrivalDate || '',
  groupShipDate: availability.groupShipDate || '',
  shipDate: availability.shipDate,
  sourceOrganizationId: availability.sourceOrganizationID?.toString() || '',
  sourceOrganizationCode: availability.sourceOrganizationCode || '',
});

export const normalizeOrderAvailability = (
  availability: OrderAvailability
) => ({
  availableQuantity: availability.availableQuantity,
  requestedDateQuantity: availability.requestedDateQuantity,
  arrivalDate: availability.arrivalDate,
  groupArrivalDate: availability.groupArrivalDate,
  groupShipDate: availability.groupShipDate,
  shipDate: availability.shipDate,
  sourceOrganizationId: availability.sourceOrganizationID,
  sourceOrganizationCode: availability.sourceOrganizationCode,
});
